import {
  activityOccurrenceStatusSchema,
  isGroup,
  type IActivityOccurrenceStatus,
  type IGroupStatus,
  type INonRoutableEmployeePerformedActivityOccurrence,
  visitStatusSchema,
} from "@medoma/models/activities";
import type { IVisit, IVisitStatus } from "@medoma/models/visits";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import {
  aggregateRequiredCompetences,
  getUnfulfilledRequirements,
  type IActivityRequirement,
} from "@medoma/models/activityRequirements";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import type { ShiftsWithContents } from "@/api/Shifts";
import type { IShiftWithContents } from "@medoma/models/shiftWithContents";

/** Same unfulfilled-requirements rules as `TimelineVisitTile` / `TimelineActivityTile` (excluding expired etc.). */
export const hasUnfulfilledRequirementsToShowForUnfinishedVisit = (
  visit: IVisit,
) => {
  const unfulfilledRequirements = getUnfulfilledRequirements({
    assignees: visit.assignees,
    requiredCompetences: aggregateRequiredCompetences(visit.occurrences),
    doubleStaffing: visit.occurrences.some(
      (occurrence) => occurrence.doubleStaffing,
    ),
  });
  return (
    unfulfilledRequirements.length > 0 &&
    visit.status !== visitStatusSchema.enum.finished
  );
};

export const getFulfilledCompetenceRequirements = ({
  requiredCompetences,
  unfulfilledRequirements,
}: {
  requiredCompetences: ReadonlyArray<IMedicalCompetence>;
  unfulfilledRequirements: ReadonlyArray<IActivityRequirement>;
}): IMedicalCompetence[] =>
  deduplicatePrimitiveArray(requiredCompetences).filter(
    (competence) => !unfulfilledRequirements.includes(competence),
  );

export const hideFullyCoveredDeletedShifts = (
  data: ShiftsWithContents,
): ShiftsWithContents => {
  const isCoveredByOthers = (o: {
    status: IVisitStatus | IActivityOccurrenceStatus | IGroupStatus | null;
    assignees: ReadonlyArray<unknown>;
  }) => o.status === visitStatusSchema.enum.finished && o.assignees.length > 1;

  const isItemCovered = (
    item: IShiftWithContents["items"][number],
  ): boolean => {
    if ("visits" in item) {
      return item.visits.every(isCoveredByOthers);
    }
    if (isGroup(item)) {
      return isCoveredByOthers({
        status: item.visitStatus ?? null,
        assignees: item.assignees,
      });
    }
    return isCoveredByOthers(item);
  };

  const isFullyCoveredDeletedShift = ({ shift, items }: IShiftWithContents) =>
    shift.deleted && items.every(isItemCovered);

  return {
    before: data.before.filter((s) => !isFullyCoveredDeletedShift(s)),
    relevant: data.relevant.filter((s) => !isFullyCoveredDeletedShift(s)),
    after: data.after.filter((s) => !isFullyCoveredDeletedShift(s)),
  };
};

export const hasUnfulfilledRequirementsToShowForUnfinishedActivityOccurrence = (
  activityOccurrence: INonRoutableEmployeePerformedActivityOccurrence,
) => {
  const unfulfilledRequirements = getUnfulfilledRequirements({
    assignees: activityOccurrence.assignees,
    requiredCompetences: activityOccurrence.requiredCompetences,
  });

  return (
    unfulfilledRequirements.length > 0 &&
    activityOccurrence.status !== activityOccurrenceStatusSchema.enum.finished
  );
};
